import { mergeClasses, tokens } from '@fluentui/react-components';
import { gemBrandColors } from '@/theme';
import { useRoomPreferenceCardStyles } from './RoomPreferenceCard.styles';

export interface RoomPreferenceCardProps {
  title: string;
  description: string;
  checked: boolean;
  maxCapacity?: number;
  disabled?: boolean;
  hideCheckbox?: boolean;
  onChange?: (checked: boolean) => void;
  className?: string;
}

export const RoomPreferenceCard = ({
  title,
  description,
  checked,
  maxCapacity,
  disabled = false,
  hideCheckbox = false,
  onChange,
  className,
}: RoomPreferenceCardProps) => {
  const styles = useRoomPreferenceCardStyles();

  const handleClick = () => {
    if (!disabled && !hideCheckbox && onChange) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || hideCheckbox) return;
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.key === ' ') {
        e.preventDefault();
      }
      onChange?.(!checked);
    }
  };

  return (
    <div
      {...(!hideCheckbox && { role: 'checkbox', 'aria-checked': checked })}
      {...(disabled && { 'aria-disabled': true })}
      tabIndex={disabled ? -1 : 0}
      className={mergeClasses(
        styles.root,
        checked && !disabled ? styles.checked : styles.unchecked,
        disabled && styles.disabled,
        !disabled && !hideCheckbox && styles.hoverable,
        className
      )}
      {...(!hideCheckbox && { onClick: handleClick, onKeyDown: handleKeyDown })}
    >
      {!hideCheckbox && (
        <svg
          className={styles.checkbox}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          {checked && !disabled ? (
            <>
              <circle cx="8" cy="8" r="8" fill={gemBrandColors[80]} />
              <path d="M4 8 L7 11 L12 5" stroke="white" strokeWidth="1.5" fill="none" />
            </>
          ) : disabled ? (
            <circle
              cx="8"
              cy="8"
              r="7"
              stroke={tokens.colorNeutralStrokeDisabled}
              fill="none"
              strokeWidth="1"
            />
          ) : (
            <circle
              cx="8"
              cy="8"
              r="7"
              stroke={tokens.colorNeutralStrokeAccessible}
              fill="none"
              strokeWidth="1"
            />
          )}
        </svg>
      )}

      <span className={mergeClasses(styles.title, disabled && styles.disabledText)}>{title}</span>

      <span className={mergeClasses(styles.description, disabled && styles.disabledText)}>
        {description}
      </span>

      {maxCapacity !== undefined && (
        <span className={mergeClasses(styles.capacity, disabled && styles.disabledText)}>
          Max: {maxCapacity}
        </span>
      )}
    </div>
  );
};

RoomPreferenceCard.displayName = 'RoomPreferenceCard';
